import { withErrorBoundary } from "@sentry/nextjs";
import Link from "next/link";
import * as React from "react";
import styled from "styled-components";
import { FullArrow } from "icons";
import { normalizeHref } from "lib/normalizeHref";
import { isBlockedPrefetchHref } from "lib/utils/isBlockedPrefetchUrl";

type Props = {
  children: React.ReactNode;
  className?: string;
  href: string;
};

export const ArrowLink: React.FC<Props> = withErrorBoundary(({ children, className = "", href, ...props }) => {
  const arrowLinkChildren = (
    <>
      <StyledLink {...props} className={className}>
        <span className="Link__text">{children}</span>
        <FullArrow className="Link__icon" />
      </StyledLink>
    </>
  );

  if (href?.[0] === "#") {
    return (
      <Link href={href} passHref {...props}>
        {arrowLinkChildren}
      </Link>
    );
  }

  if (href?.includes("mailto:") || isBlockedPrefetchHref(href)) {
    return (
      <StyledLink {...props} as="a" className={className} href={href} rel="noopener noreferrer" target="_blank">
        {arrowLinkChildren}
      </StyledLink>
    );
  }

  return (
    <Link href={normalizeHref(href)} passHref {...props}>
      {arrowLinkChildren}
    </Link>
  );
}, {});

const StyledLink = styled.div`
  display: flex;
  align-items: center;
  width: fit-content;
  text-decoration: none;

  :hover {
    text-decoration: none;
    cursor: pointer;

    & > .Link__icon {
      left: 4px;
    }
  }

  & > div {
    color: inherit;
  }

  .Link {
    display: flex;
    align-items: center;
  }

  .Link__text {
    margin-right: 4px;
  }

  /* stylelint-disable-next-line no-descending-specificity */
  .Link__icon {
    position: relative;
    left: 0;
    flex-shrink: 0;
    width: 18px;
    height: 18px;
    transition: all 0.15s ease-in;
    fill: ${({ theme }) => theme.colors.fog["80"]};
  }

  .Link:hover {
    text-decoration: underline;
    cursor: pointer;

    & > .Link__icon {
      left: 4px;
    }
  }

  /* stylelint-disable-next-line no-duplicate-selectors */
  & > div {
    color: inherit;
  }
`;
